import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatExpansionPanel} from '@angular/material/expansion';
import {DocumentTypePickerComponent, FilterLabel, IDocumentType} from 'document-type-picker-lib';
import {Subscription} from 'rxjs';
import {BullhornEntityType} from '../../../../types/bullhorn/bullhorn-entity-type';
import {DateFilterType, IDateFilter, IFilter, SortFilterType} from '../../../../types/prompt-profile';

@Component({
    selector: 'app-filter-panel',
    templateUrl: './filter-panel.component.html',
    styleUrls: ['./filter-panel.component.scss'],
    standalone: false
})
export class FilterPanelComponent implements OnInit, AfterViewInit {
  @Input() filter: IFilter = {};
  @Output() filterChange = new EventEmitter<IFilter>();
  @Input() entityType?: BullhornEntityType;

  @ViewChild('expansionPanel') expansionPanel: MatExpansionPanel | undefined;
  @ViewChild('chipsContainer') chipsContainer: ElementRef | undefined;
  @ViewChild('documentTypePicker') documentTypePicker: DocumentTypePickerComponent | undefined;

  private documentTypePickerSubscription: Subscription | undefined;
  documentTypes: IDocumentType[] = [];

  @Input() filterLabel?: FilterLabel;
  protected placeHolderText = '';

  defaultDateFilter: IDateFilter = {rangeType: DateFilterType.LATEST, timeAmount: 14};
  protected predefinedDates: any[] = [];
  private dateSortOptions = {newest: 'Newest first', oldest: 'Oldest first'};
  protected dateSortOptionsArray = Object.values(this.dateSortOptions);
  protected readonly BullhornEntityType = BullhornEntityType;
  protected readonly FilterLabel = FilterLabel;

  get filterEnabled(): boolean {
    return this.filter.isFilterEnabled ?? true;
  }
  set filterEnabled(value: boolean) {
    this.filter.isFilterEnabled = value;
    if (value) {
      this.expansionPanel?.open();
    } else if (this.expansionPanel?.expanded) {
      this.expansionPanel.close();
    }
    this.emitFilterChange();
  }

  get filterDateTimeAmount(): number | undefined {
    return this.filter.date?.timeAmount;
  }
  set filterDateTimeAmount(value: number | undefined) {
    if (!this.filter.date) {
      this.filter.date = this.defaultDateFilter;
    }
    this.filter.date.timeAmount = value;
    this.emitFilterChange();
  }

  get filterSort(): string {
    return this.filter.sort === SortFilterType.OLDEST ? this.dateSortOptions.oldest : this.dateSortOptions.newest;
  }
  set filterSort(value: string) {
    this.filter.sort = value === this.dateSortOptions.oldest ? SortFilterType.OLDEST : SortFilterType.NEWEST;
    this.emitFilterChange();
  }

  get filterCount(): number {
    return this.filter.count ?? 15;
  }
  set filterCount(value: number) {
    this.filter.count = +value;
    this.emitFilterChange();
  }

  get filterAutoInclude(): boolean {
    return this.filter.autoInclude ?? false;
  }
  set filterAutoInclude(value: boolean) {
    this.filter.autoInclude = value;
    this.emitFilterChange();
  }

  constructor() { }

  ngOnInit(): void {
    this.predefinedDates.push(
      { label: `All ${this.filterLabel ? this.filterLabel + 's' : ''}`, value: -1 },
      { label: 'Last 1 day', value: 1},
      { label: 'Last 14 days', value: 14},
      { label: 'Last 30 days', value: 30},
      { label: 'Last 180 days', value: 180 },
      { label: 'Last 1 year', value: 365 },
    );
    if (!this.filter) {
      this.filter = {};
    }
    if (this.filterLabel === FilterLabel.NOTE && this.filter.isNotesEnabled !== undefined) {
      this.filter.isFilterEnabled = this.filter.isNotesEnabled ?? true;
      this.filter.isNotesEnabled = undefined;
    } else {
      this.filter.isFilterEnabled = this.filter.isFilterEnabled ?? true;
    }
    this.filter.category = this.filter.category ?? [];
    this.filter.date = this.filter.date ?? this.defaultDateFilter;
    this.filter.sort = this.filter.sort ?? SortFilterType.NEWEST;
    this.filter.count = this.filter.count ?? 15;
    this.filter.autoInclude = this.filter.autoInclude ?? false;

    this.placeHolderText = 'Enter ' + (this.filterLabel === FilterLabel.FILE ? 'type' : 'action') + ' to filter';
  }

  ngAfterViewInit(): void {
    this.loadDocumentTypeFilters();
  }

  private loadDocumentTypeFilters(): void {
    // Add all the chips
    if (this.filter.category?.length) {
      this.filter.category.forEach((category) => {
        this.documentTypes.push({id: category, label: category});
      });
    }
    // Remove any added chips from the drop down
    this.documentTypePickerSubscription = this.documentTypePicker?.observeReady().subscribe({
      next: _ => {
        this.documentTypes.forEach(dt => this.documentTypePicker?.removeItem(dt.id));
      }
    });
  }

  private emitFilterChange(): void {
    this.filterChange.emit(this.filter);
  }

  protected getLabelByValue(value: number | undefined): string {
    const date = this.predefinedDates.find(item => item.value === value);
    return date?.label ?? '';
  }

  onSelected(documentType: IDocumentType | undefined): void {
    if (documentType?.id) {
      if (!this.documentTypes.find(dt => dt.id === documentType.id)) {
        this.documentTypes.push(documentType);
        this.documentTypes.sort((a, b) => a.label.localeCompare(b.label));
        this.documentTypePicker?.removeItem(documentType.id);
        this.filter.category?.push(documentType.label);
        this.emitFilterChange();
      }
    }
  }

  onRemove(documentType: IDocumentType): void {
    this.documentTypes = this.documentTypes.filter(dt => dt.id !== documentType.id);
    this.documentTypes.sort((a, b) => a.label.localeCompare(b.label));
    if (!documentType.custom) {
      this.documentTypePicker?.addItem(documentType);
    }
    if (this.filter.category) {
      const index = this.filter.category.indexOf(documentType.label);
      if (index !== -1) {
        this.filter.category.splice(index, 1);
        this.emitFilterChange();
      }
    }
  }
}
